import { useCallback, useRef, useState } from "react";

import { API_BASE } from "../api";

// ProfileEditPatch is the wire shape for PATCH /api/profile.
//
// SILL-302: household_income / savings / monthly_debts are intentionally
// absent. The backend rejects them with 400 aggregate_row_not_editable so
// callers must use the manual-entries API via useManualEntries. Keeping
// them out of the TypeScript type means a stray call site fails to compile
// rather than discovering the policy at runtime.
export interface ProfileEditPatch {
  credit_score_range?: string;
  employment_months?: number;
  monthly_budget?: number;
  reserve_floor_months?: number;
  comfort_dti_pct?: number | null;
  // SILL-537 M5 (vf-shape-editable): one of the six canonical income-shape
  // values (see model.ValidIncomeShapes server-side); the backend rejects
  // anything else with 400. A row edit here re-runs
  // verificationflow.PlanVerificationPath on the caller's NEXT read of
  // GET /api/verification/path — that read is live, so there is no
  // separate invalidation step.
  income_shape?: string;
}

export interface PatchProfileResponse {
  profile: unknown;
  snapshot: { id: string; [k: string]: unknown };
  financials: unknown;
  readiness: unknown;
}

export interface UseProfileEditOptions {
  getToken: () => Promise<string | null>;
  // Snapshot id is required for persisted edits — the backend rejects
  // empty/null base_snapshot_id with 422. Profile rows may still offer a
  // local-only preview editor when this option object is absent.
  getBaseSnapshotId: () => string;
  onSuccess?: (data: PatchProfileResponse) => void;
  onStaleRebase?: (latestSnapshot: { id: string }) => void;
}

export interface UseProfileEditResult {
  mutate: (
    patch: ProfileEditPatch,
    options?: { forceDowntier?: boolean },
  ) => Promise<void>;
  isPending: boolean;
  error: Error | null;
}

export class StaleSnapshotError extends Error {
  readonly code = "stale_snapshot" as const;
  constructor(public latestSnapshot: { id: string } | null) {
    super("stale snapshot");
    this.name = "StaleSnapshotError";
  }
}

export class AggregateRowError extends Error {
  readonly code = "aggregate_row_not_editable" as const;
  constructor(public field: string | null) {
    super(`aggregate row not editable: ${field ?? "<unknown>"}`);
    this.name = "AggregateRowError";
  }
}

export function useProfileEdit(
  opts: UseProfileEditOptions,
): UseProfileEditResult {
  const { getToken, getBaseSnapshotId, onSuccess, onStaleRebase } = opts;
  const [isPending, setIsPending] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  // Single-shot in-flight guard. See useTargetEdit for rationale.
  const inFlightRef = useRef(false);

  const mutate = useCallback(
    async (
      patch: ProfileEditPatch,
      options?: { forceDowntier?: boolean },
    ): Promise<void> => {
      // Drop new calls while a mutation is in flight. Phase 6c row components
      // fire mutate from onBlur, where rapid blurs are plausible.
      if (inFlightRef.current) {
        return;
      }
      inFlightRef.current = true;
      setIsPending(true);
      setError(null);
      try {
        const token = await getToken();
        const baseSnapshotID = getBaseSnapshotId();

        // Body shape matches `patchProfileRequest` exactly:
        // { base_snapshot_id, patch, force_downtier }
        // The patch is nested — NOT flattened — per the backend contract.
        const body: Record<string, unknown> = {
          base_snapshot_id: baseSnapshotID,
          patch,
          force_downtier: options?.forceDowntier ?? false,
        };

        const res = await fetch(`${API_BASE}/api/profile`, {
          method: "PATCH",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify(body),
        });

        if (!res.ok) {
          const errBody = (await res.json().catch(() => ({}))) as Record<
            string,
            unknown
          >;
          if (res.status === 409 && errBody.code === "stale_snapshot") {
            const latestSnapshot = (errBody.snapshot as { id: string }) ?? null;
            if (latestSnapshot && onStaleRebase) {
              try {
                onStaleRebase(latestSnapshot);
              } catch (cbErr) {
                console.error(
                  "useProfileEdit onStaleRebase callback threw:",
                  cbErr,
                );
              }
            }
            throw new StaleSnapshotError(latestSnapshot);
          }
          if (
            res.status === 400 &&
            errBody.code === "aggregate_row_not_editable"
          ) {
            throw new AggregateRowError((errBody.field as string) ?? null);
          }
          throw new Error((errBody?.message as string) ?? `HTTP ${res.status}`);
        }

        const data = (await res.json()) as PatchProfileResponse;
        if (onSuccess) {
          try {
            onSuccess(data);
          } catch (cbErr) {
            // Network 200 succeeded; a callback throw is a caller bug, not a
            // mutation failure. Do not surface to error state.
            console.error("useProfileEdit onSuccess callback threw:", cbErr);
          }
        }
      } catch (e) {
        const err = e instanceof Error ? e : new Error(String(e));
        setError(err);
        throw err;
      } finally {
        setIsPending(false);
        inFlightRef.current = false;
      }
    },
    [getToken, getBaseSnapshotId, onSuccess, onStaleRebase],
  );

  return { mutate, isPending, error };
}
